import React, { useState, useEffect } from "react";
import { Edit2, Trash2, Plus, User, Mail, Phone, Calendar } from "lucide-react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import Loader from "../../components/common/Loader";

const Applications = () => {
  const [applications, setApplications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const fetchApplications = async () => {
    try {
      const config = {
        method: "get",
        maxBodyLength: Infinity,
        url: `${process.env.REACT_APP_API_URL}/application/get-all-applications`,
        headers: {},
      };
      const startTime = Date.now();

      const response = await axios.request(config);

      if (response.data.success) {
        setApplications(response.data.data);
      }
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(1000 - elapsed, 0);

      setTimeout(() => setIsLoading(false), remaining);
    } catch (err) {
      console.error("Error fetching applications:", err);
      setError(err.message);
      setIsLoading(false);
      setTimeout(() => setIsLoading(false), 1000);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, []);

  const handleCardClick = (applicationId) => {
    navigate("/application-details", {
      state: { applicationId: applicationId },
    });
  };

  const handleEdit = (e, applicationId) => {
    e.stopPropagation();
    navigate(`/edit-application/${applicationId}`);
    console.log("Edit application:", applicationId);
  };

  const handleDelete = (e, applicationId) => {
    e.stopPropagation();

    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        let config = {
          method: "delete",
          maxBodyLength: Infinity,
          url: `${process.env.REACT_APP_API_URL}/application/delete-application/${applicationId}`,
          headers: {},
        };

        axios
          .request(config)
          .then((response) => {
            console.log("Deleted:", response.data);
            Swal.fire({
              title: "Deleted!",
              text: "The application has been deleted successfully.",
              icon: "success",
              timer: 1500,
              showConfirmButton: false,
            });
            fetchApplications();
          })
          .catch((error) => {
            console.error("Delete error:", error);
            Swal.fire({
              title: "Error!",
              text: "Something went wrong while deleting the application.",
              icon: "error",
            });
          });
      } else {
        Swal.fire({
          title: "Cancelled",
          text: "Your application is safe 😊",
          icon: "info",
          timer: 1200,
          showConfirmButton: false,
        });
      }
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <div className="min-h-screen p-6 dashboardContainer">
      <div className="w-full mx-auto">
        <div className="mb-4 mt-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-primaryTextColor mb-2">
                Application Management
              </h1>
            </div>
          </div>
        </div>

        {/* Applications Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {isLoading ? (
            <>
              <Loader />
            </>
          ) : error ? (
            <div className="col-span-full text-center py-16">
              <div className="text-6xl mb-4">⚠️</div>
              <h3 className="text-2xl font-bold text-primaryTextColor mb-2">
                Error Loading Applications
              </h3>
              <p className="text-secondaryTextColor">{error}</p>
            </div>
          ) : (
            applications.map((application) => (
              <div
                key={application._id}
                onClick={() => handleCardClick(application?._id)}
                className="bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer group relative overflow-hidden"
              >
                <div className="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10">
                  <button
                    onClick={(e) => handleEdit(e, application?._id)}
                    className="bg-secondaryColor text-white p-2 rounded-full hover:bg-opacity-90 transition-all shadow-md"
                  >
                    <Edit2 size={16} />
                  </button>
                  <button
                    onClick={(e) => handleDelete(e, application?._id)}
                    className="bg-primaryColor text-white p-2 rounded-full hover:bg-opacity-90 transition-all shadow-md"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>

                <div className="p-6">
                  {/* Course Category Badge */}
                  <span className="inline-block bg-secondaryColor text-white text-xs font-semibold px-3 py-1 rounded-full mb-3 uppercase">
                    {application?.course?.category}
                  </span>

                  {/* Applicant Name */}
                  <h3 className="text-xl font-bold text-primaryTextColor mb-2 group-hover:text-primaryColor transition-colors flex items-center gap-2">
                    <User size={20} />
                    {application?.name}
                  </h3>

                  {/* Contact Information */}
                  <div className="space-y-2 mb-4">
                    <p className="text-secondaryTextColor text-sm flex items-center gap-2">
                      <Mail size={14} className="text-primaryColor" />
                      {application?.email}
                    </p>
                    <p className="text-secondaryTextColor text-sm flex items-center gap-2">
                      <Phone size={14} className="text-primaryColor" />
                      {application?.mobileNumber}
                    </p>
                    <p className="text-secondaryTextColor text-sm flex items-center gap-2">
                      <Calendar size={14} className="text-primaryColor" />
                      DOB: {formatDate(application?.dateOfBirth)}
                    </p>
                  </div>

                  {/* Application Details */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    <span className="bg-gray-100 text-primaryTextColor text-xs px-3 py-1 rounded-full">
                      {application?.gender === "M" ? "Male" : "Female"}
                    </span>
                    <span className="bg-gray-100 text-primaryTextColor text-xs px-3 py-1 rounded-full">
                      {application?.yearOfPassing}
                    </span>
                  </div>

                  {/* Location & Branch */}
                  <div className="border-t pt-4">
                    <p className="text-secondaryTextColor text-sm">
                      <span className="font-semibold">Branch:</span>{" "}
                      {application?.centreBranchName}
                    </p>
                    <p className="text-secondaryTextColor text-sm mt-1">
                      <span className="font-semibold">City:</span>{" "}
                      {application?.centreCityName}
                    </p>
                    <p className="text-secondaryTextColor text-sm mt-1">
                      <span className="font-semibold">Qualification:</span>{" "}
                      {application?.lastAcademicQualification}
                    </p>
                  </div>
                </div>

                {/* Bottom Border Accent */}
                <div className="h-1 bg-gradient-to-r from-primaryColor to-secondaryColor transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></div>
              </div>
            ))
          )}
        </div>

        {/* Empty State */}
        {!isLoading && applications.length === 0 && (
          <div className="text-center py-16">
            <div className="text-6xl mb-4">📋</div>
            <h3 className="text-2xl font-bold text-primaryTextColor mb-2">
              No Applications Found
            </h3>
            <p className="text-secondaryTextColor mb-6">
              Start by adding your first application
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Applications;
